<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// State
const isLoading = ref(true);
const currentConversation = ref(null);
const messages = ref([]);

// Computed
const conversations = computed(() => store.getters.getAllConversations);

const conversationIdInt = computed(() => parseInt(props.conversationId, 10));

const conversation = computed(() => {
  return (
    currentConversation.value ||
    conversations.value.find(c => c.id === conversationIdInt.value)
  );
});

const shouldShowSidebar = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  return isContactSidebarOpen !== false;
});

const statusConfig = computed(() => {
  if (!conversation.value)
    return { class: 'bg-n-slate-3 text-n-slate-11', label: '' };

  const configs = {
    open: {
      class: 'bg-n-green-3 text-n-green-11',
      label: t('KANBAN_VIEW.OPEN'),
    },
    resolved: {
      class: 'bg-n-blue-3 text-n-blue-11',
      label: t('KANBAN_VIEW.RESOLVED'),
    },
    pending: {
      class: 'bg-n-amber-3 text-n-amber-11',
      label: t('KANBAN_VIEW.PENDING'),
    },
    snoozed: {
      class: 'bg-n-slate-3 text-n-slate-11',
      label: t('KANBAN_VIEW.SNOOZED'),
    },
  };

  return configs[conversation.value.status] || configs.snoozed;
});

// Methods
const handleClose = () => {
  emit('close');
};

const handleKeyDown = event => {
  if (event.key === 'Escape' && !event.shiftKey && !event.ctrlKey) {
    handleClose();
  }
};

const toggleSidebar = () => {
  const currentState = uiSettings.value.is_contact_sidebar_open;
  updateUISettings({
    is_contact_sidebar_open: !currentState,
  });
};

const markConversationAsRead = async () => {
  if (!conversation.value || conversation.value.unread_count === 0) {
    return;
  }

  try {
    await store.dispatch('markMessagesRead', {
      id: conversationIdInt.value,
    });
  } catch {
    // Mark as read failed silently - non-critical
  }
};

const loadConversation = async () => {
  try {
    isLoading.value = true;

    let conv = conversations.value.find(c => c.id === conversationIdInt.value);

    if (!conv) {
      await store.dispatch('getConversation', conversationIdInt.value);
      conv = conversations.value.find(c => c.id === conversationIdInt.value);
    }

    if (conv) {
      currentConversation.value = { ...conv };

      await store.dispatch('setActiveChat', { data: conv });
      await store.commit('SET_CURRENT_CHAT_WINDOW', conv);

      if (conv.inbox_id) {
        await store.dispatch('inboxAssignableAgents/fetch', [conv.inbox_id]);
        await store.dispatch('inboxes/get');
      }

      if (!conv.messages || conv.messages.length === 0) {
        try {
          await store.dispatch('fetchPreviousMessages', {
            conversationId: conversationIdInt.value,
          });

          const updated = conversations.value.find(
            c => c.id === conversationIdInt.value
          );
          if (updated && updated.messages) {
            currentConversation.value = { ...updated };
            messages.value = updated.messages;
          }
        } catch {
          // Messages fetch failed silently
        }
      } else {
        messages.value = conv.messages;
      }

      if (conversationIdInt.value) {
        try {
          await store.dispatch(
            'conversationLabels/get',
            conversationIdInt.value
          );
        } catch {
          // Labels fetch failed silently
        }
      }
    } else {
      throw new Error('Conversation not found');
    }

    markConversationAsRead();
  } catch {
    currentConversation.value = null;
    messages.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  await loadConversation();
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown);
  if (conversationIdInt.value) {
    store.dispatch('clearSelectedState');
  }
});
</script>

<template>
  <!-- Modal Overlay -->
  <div
    class="fixed inset-0 z-[999] flex items-center justify-center bg-n-alpha-black2 backdrop-blur-sm"
    @click.self="handleClose"
  >
    <!-- Modal Container -->
    <div
      class="relative w-[85vw] h-[80vh] max-w-[1400px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-2xl overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0"
      >
        <div class="flex items-center gap-3">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CONVERSATION.TITLE_WITH_ID', { id: conversationId }) }}
          </h3>
          <span
            v-if="conversation"
            class="px-2 py-0.5 rounded-full text-xs font-medium"
            :class="statusConfig.class"
          >
            {{ statusConfig.label || conversation.status }}
          </span>
          <span
            v-if="conversation?.inbox"
            class="flex items-center gap-1 text-xs text-n-slate-10"
          >
            <Icon icon="i-lucide-inbox" class="size-3.5" />
            {{ conversation.inbox.name }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <!-- Toggle Sidebar -->
          <Button
            variant="faded"
            color="slate"
            size="sm"
            :icon="
              shouldShowSidebar
                ? 'i-lucide-panel-right-close'
                : 'i-lucide-panel-right-open'
            "
            @click="toggleSidebar"
          />
          <!-- Close -->
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            @click="handleClose"
          />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex-1 flex items-center justify-center">
        <div class="text-center">
          <Icon
            icon="i-lucide-loader-2"
            class="size-8 text-n-blue-10 animate-spin mx-auto mb-3"
          />
          <p class="text-sm text-n-slate-11">
            {{ t('CONVERSATION.LOADING') }}
          </p>
        </div>
      </div>

      <!-- Content -->
      <div
        v-else-if="conversation && conversation.id === conversationIdInt"
        class="flex-1 flex overflow-hidden min-h-0"
      >
        <!-- Conversation Area -->
        <div class="flex-1 flex flex-col min-w-0">
          <ConversationBox
            v-if="conversation && conversation.id"
            :inbox-id="conversation.inbox_id"
            :is-contact-panel-open="shouldShowSidebar"
            :is-on-expanded-layout="false"
            :is-inbox-view="false"
            class="flex-1"
          />
        </div>

        <!-- Sidebar -->
        <ConversationSidebar
          v-if="shouldShowSidebar && conversation"
          :current-chat="conversation"
          class="w-[340px] flex-shrink-0 border-l border-n-weak"
        />
      </div>

      <!-- Error State -->
      <div v-else class="flex-1 flex items-center justify-center">
        <div class="text-center px-6">
          <Icon
            icon="i-lucide-alert-triangle"
            class="size-12 text-n-amber-10 mx-auto mb-4"
          />
          <p class="text-base font-medium text-n-slate-12 mb-2">
            {{ t('CONVERSATION.NOT_FOUND') }}
          </p>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ t('CONVERSATION.NOT_FOUND_ID', { id: conversationId }) }}
          </p>
          <Button
            color="blue"
            icon="i-lucide-refresh-cw"
            :label="t('CONVERSATION.RETRY')"
            @click="loadConversation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Garantir altura completa para os componentes */
:deep(.conversation-panel) {
  height: 100% !important;
}

:deep(.conversations-sidebar) {
  height: 100% !important;
}

/* Remover botão de voltar do header */
:deep(.back-button),
:deep([class*='back-button']) {
  display: none !important;
}

/* Modal de mídia acima do modal de conversa */
:deep(.modal-container),
:deep(.woot-modal),
:deep(.image-modal) {
  z-index: 9999 !important;
}

/* Emoji picker posição */
:deep(.emoji-dialog),
:deep(.emoji-picker) {
  bottom: 100% !important;
  top: auto !important;
  z-index: 1000 !important;
}
</style>
